import Vue from 'vue';
import Component from 'vue-class-component';
import './switch-button.component.scss';
import { Prop, Emit } from 'vue-property-decorator';

@Component({})
export default class SwitchButtonComponent extends Vue {
  mouseIsDown = false;
  initialMouse = 0;
  slideMovementTotal = 0;
  mouseIsOutBrowser = false;

  @Prop() active!: boolean;
  @Prop() slider!: boolean;
  @Prop() title!: string;

  @Prop() leftLabel!: string;
  @Prop({ default: 0 }) leftValue!: number;

  @Prop() rightLabel!: string;
  @Prop({ default: 1 }) rightValue!: number;

  mounted() {
    document?.body?.addEventListener('mouseup', this.handleOnDragEnd);
    document?.body?.addEventListener('touchend', this.handleOnDragEnd);
    document?.body?.addEventListener('mousemove', this.handleOnMove);
    document?.body?.addEventListener('touchmove', this.handleOnMove);
    document?.body?.addEventListener('mouseleave', this.handleMoveOutBrowser);
  }

  beforeDestroy() {
    document?.body?.removeEventListener('mouseup', this.handleOnDragEnd);
    document?.body?.removeEventListener('touchend', this.handleOnDragEnd);
    document?.body?.removeEventListener('mousemove', this.handleOnMove);
    document?.body?.removeEventListener('touchmove', this.handleOnMove);
    document?.body?.removeEventListener('mouseleave', this.handleMoveOutBrowser);
  }

  /**
   * 変更時の処理を実行します。
   *
   * @param targetValue - 切り替えるターゲットの値
   * @emit change - ターゲットが切り替えられたことを示すイベント
   * @returns {number} - 切り替えられたターゲットの値
   */
  @Emit('change')
  private onChange(targetValue: number): number {
    return targetValue;
  }

  /**
   * ドラッグイベントを処理するメソッドです。
   *
   * @param event - マウスイベントまたはタッチイベント
   */
  private handleOnDrag(event: MouseEvent | TouchEvent): void {
    this.mouseIsDown = true;
    document?.body?.addEventListener('mouseup', this.handleOnDragEnd, false);
    document?.body?.addEventListener('touchend', this.handleOnDragEnd, false);
    document?.body?.addEventListener('mousemove', this.handleOnMove, false);
    document?.body?.addEventListener('touchmove', this.handleOnMove, false);
    this.slideMovementTotal =
      (document.getElementById('switch-btn') as HTMLElement)?.clientWidth -
      (this.$refs.sliderStransition as HTMLDivElement)?.clientWidth;
    this.initialMouse = (event as MouseEvent).clientX || (event as TouchEvent).touches[0].pageX;
  }

  /**
   * ドラッグ終了イベントを処理するメソッドです。
   *
   * @param event - マウスイベントまたはタッチイベント
   */
  private handleOnDragEnd(event: MouseEvent | TouchEvent): void {
    if (!this.mouseIsDown) return;
    this.mouseIsDown = false;
    document?.body?.removeEventListener('mouseup', this.handleOnDragEnd);
    document?.body?.removeEventListener('touchend', this.handleOnDragEnd);
    document?.body?.removeEventListener('mousemove', this.handleOnMove);
    document?.body?.removeEventListener('touchmove', this.handleOnMove);
    if (this.mouseIsOutBrowser) {
      this.mouseIsOutBrowser = false;
      return;
    }
    const currentMouse = (event as MouseEvent).clientX || (event as TouchEvent).changedTouches[0].pageX;
    const relativeMouse = this.active ? currentMouse - this.initialMouse : this.initialMouse - currentMouse;
    if (relativeMouse < this.slideMovementTotal / 2) {
      this.smoothSlider(this.active ? '0' : '50%');
    } else {
      this.smoothSlider(this.active ? '50%' : '0');
      this.onChange(this.active ? this.rightValue : this.leftValue);
    }
  }

  /**
   * スライダーをスムーズに移動させるメソッドです。
   *
   * @param value - 移動先の値
   */
  private smoothSlider(value: string) {
    const slider = document.getElementsByName('transition') as NodeListOf<HTMLDivElement>;
    slider.forEach((element) => {
      const animation = element.animate(
        {
          left: [element.style.left, value]
        },
        300
      );
      animation.onfinish = () => {
        animation.cancel();
        element.style.left = value;
      };
      element.style.left = value;
    });
  }

  /**
   * スライダーを移動させるメソッドです。
   *
   * @param event - マウスイベントまたはタッチイベント
   */
  private handleOnMove(event: MouseEvent | TouchEvent): void {
    if (!this.mouseIsDown) return;
    if (this.mouseIsOutBrowser) {
      this.mouseIsOutBrowser = false;
      return;
    }
    const slider = document.getElementsByName('transition') as NodeListOf<HTMLDivElement>;
    const currentMouse = (event as MouseEvent).clientX || (event as TouchEvent).changedTouches[0].pageX;
    const relativeMouse = currentMouse - this.initialMouse;
    const left = !this.active ? this.slideMovementTotal + relativeMouse : relativeMouse;
    if (left < 0 || left > this.slideMovementTotal) {
      return;
    }
    slider.forEach((element) => {
      element.style.left = left + 'px';
    });
  }

  /**
   * ブラウザ外にマウスが移動した際の処理を行うメソッドです。
   *
   * @param event - マウスイベント
   */
  private handleMoveOutBrowser(event: MouseEvent): void {
    if (!this.mouseIsDown) return;
    if (
      event?.clientY <= 0 ||
      event?.clientX <= 0 ||
      event?.clientX >= window.innerWidth ||
      event?.clientY >= window.innerHeight
    ) {
      this.mouseIsOutBrowser = true;
      this.mouseIsDown = false;
      const slider = document.getElementsByName('transition') as NodeListOf<HTMLDivElement>;
      slider.forEach((element) => {
        element.style.left = this.active ? '0' : '50%';
      });
      return;
    }
  }

  render() {
    return (
      <div class="switch-btn-container">
        {this.slider && <span class="text-size-14 font-weight-bold d-flex align-items-center">{this.title}</span>}
        <div class="btn-group" role="group">
          <div class="switch-btn" id="switch-btn">
            <div class="left-btn">
              <button type="button" class="font-weight-bold" onClick={() => this.onChange(this.leftValue)}>
                {this.leftLabel}
              </button>
            </div>
            <div class="right-btn">
              <button type="button" class="font-weight-bold" onClick={() => this.onChange(this.rightValue)}>
                {this.rightLabel}
              </button>
            </div>
            <div class={['transition-switching']} name="transition" style={{ left: this.active ? '0' : '50%' }} />
            {this.slider && (
              <div
                onMousedown={(e: MouseEvent) => this.handleOnDrag(e)}
                onTouchstart={(e: TouchEvent) => this.handleOnDrag(e)}
                onMousemove={(e: MouseEvent) => this.handleOnMove(e)}
                onTouchmove={(e: TouchEvent) => this.handleOnMove(e)}
                onMouseup={(e: MouseEvent) => this.handleOnDragEnd(e)}
                onTouchend={(e: TouchEvent) => this.handleOnDragEnd(e)}
                ref="sliderStransition"
                name="transition"
                class={['slider-switching', { 'cursor-pointer': true }]}
                style={{ left: this.active ? '0' : '50%' }}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}
